<template>
  <section>
    <div
      v-if="isTextEntry(quiz) && answerValue"
      class="quiz-result__entered-answer container-sm"
    >
      <div class="quiz-result__entered-answer-title">
        {{ $t('common.modal.preview.entered_answer') }}
      </div>
      <div class="quiz-result__entered-answer-value">
        {{ answerValue }}
      </div>
    </div>
    <div class="quiz-result">
      <div class="container-sm">
        <div class="quiz-result__content row">
          <div class="col-6 quiz-result-answer">
            <template v-if="isVaildSelectedOptionIndex && isCorrect">
              <img
                src="@/assets/images/quiz-correct-s.svg"
                class="quiz-correct-s inline-block mr-6"
              >
              <strong class="quiz-result__label">
                {{ $t('viewer.page.quiz.result.correct') }}
              </strong>
            </template>
            <template v-if="isVaildSelectedOptionIndex && !isCorrect">
              <img
                src="@/assets/images/quiz-wrong-s.svg"
                class="quiz-wrong-s inline-block mr-6"
              >
              <strong class="quiz-result__label">
                {{ $t('viewer.page.quiz.result.wrong') }}
              </strong>
            </template>
          </div>
          <div
            v-if="isMultipleChoice(quiz) && !isOXQuiz"
            class="col-6 quiz-result__description"
          >
            <span
              v-if="isVaildSelectedOptionIndex"
              class="quiz-result__selected"
              :class="quizResultSelected(isCorrect)"
            >
              <span>
                {{ $t('common.modal.preview.chosen_answer') }}
                {{ $t('common.modal.preview.answer_number', { n: selectedOptionIndex }) }}
              </span>
            </span>
            <span
              v-if="isVaildCorrectOptionIndex"
              class="quiz-result__answer"
            >
              {{ $t('common.modal.preview.correct_answer') }}
              <span>
                {{ $t('common.modal.preview.answer_number', { n: correctOptionIndex }) }}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isTextEntry(quiz)"
      class="quiz-result__answer-content container-sm"
    >
      <div class="quiz-result__answer-content-title">
        {{ $t('common.modal.preview.correct_answer') }}
      </div>
      <div class="quiz-result__answer-content-value">
        {{ correctAnswerContents }}
      </div>
    </div>
  </section>
</template>

<script>
import { isNil, orderBy, isEmpty, findIndex, find } from 'lodash';

import quiz from '@/mixins/quiz';

export default {
  name: 'quiz-result',

  mixins: [quiz],

  props: {
    quiz: {
      type: Object,
      default: () => {},
    },
    quizResult: {
      type: Object,
      default: () => {},
    },
    isOXQuiz: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    pageType() {
      return this.quiz.page_type.name;
    },

    quizOptions() {
      return orderBy(this.quiz.answers, ['id'], ['asc']);
    },

    correctAnswerOption() {
      const answerOption = find(this.quizOptions, { is_correct: true });
      if (!isEmpty(answerOption)) return answerOption;
      return {};
    },

    correctOptionIndex() {
      if (isEmpty(this.quizOptions)) {
        return 0;
      }

      return findIndex(this.quizOptions, option => (
        option.id === this.correctAnswerOption.id
      )) + 1;
    },

    isVaildCorrectOptionIndex() {
      return !isNil(this.correctOptionIndex);
    },

    selectedOptionIndex() {
      if (isEmpty(this.quizOptions)) {
        return 0;
      }

      return findIndex(this.quizOptions, option => (
        option.id === this.quizResult.answer_id
      )) + 1;
    },

    isVaildSelectedOptionIndex() {
      return !isNil(this.selectedOptionIndex) && this.selectedOptionIndex > 0;
    },

    correctAnswerContents() {
      if (isEmpty(this.correctAnswerOption)) {
        return null;
      }

      return this.correctAnswerOption.content;
    },

    answerValue() {
      if (isEmpty(this.quizResult)) return null;
      return this.quizResult.value;
    },

    isCorrect() {
      if (isEmpty(this.quizResult)) return false;
      return this.quizResult.is_correct;
    },
  },

  methods: {
    quizResultSelected(isCorrect) {
      return isCorrect ? '' : 'active';
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../assets/styles/components/_typographies';
  @import '../assets/styles/components/_breakpoints';

  .quiz-result {
    background-color: rgba(0,0,0,.02);
    border: 1px solid rgba(0,0,0,.12);
    border-style: solid none;
  }

  .quiz-result__content {
    max-width: 570px;
    margin: 0 auto;
    padding: 20px 0;
    position: relative;

    .quiz-correct-s,
    .quiz-wrong-s {
      width: 24px;
      height: 24px;
    }
  }

  .quiz-result__label {
    font-size: 17px;
    font-weight: 400;

    @media (min-width: $desktop-breakpoint) {
      font-size: 20px;
    }
  }

  .quiz-result-answer {
    display: flex;
    align-items: center;
    padding: 0;
  }

  .quiz-result__description {
    line-height: 2;
    margin: 0;
    padding: 0;
    text-align: right;
    font-size: 14px;
    line-height: 1.43;

    @media (min-width: $desktop-breakpoint) {
      font-size: 18px;
    }

    >.quiz-result__selected {
      display: inline-block;
      padding-left: 12px;
    }

    .active{
      color: #ff595f;
    }

    >.quiz-result__answer {
      display: inline-block;
      padding-left: 12px;
      color: #03a9f4;
    }
  }

  .quiz-result__entered-answer {
    padding: 16px;

    .quiz-result__entered-answer-title {
      font-size: 14px;
      font-weight: bold;
      color: rgba(0, 0, 0, 0.54);
      padding: 6px 0px 6px 0px;
    }

    .quiz-result__entered-answer-value {
      background-color: #f5f5f5;
      border: solid 1px #eeeeee;
      border-radius: 4px;
      padding: 14px;
      font-size: 14px;
    }
  }

  .quiz-result__answer-content {
    padding: 14px;

    .quiz-result__answer-content-title {
      font-size: 14px;
      font-weight: bold;
      color: #03a9f4;
      padding: 6px 0px 6px 0px;
    }
  }
</style>
